import { useQuery } from '@tanstack/react-query'
import type { Dashboard as DashType } from '../types'
import * as api from '../api/client'
import Sparkline from './Sparkline'

const CATEGORY_COLORS: Record<string, string> = {
  People: 'bg-sky-100 text-sky-800',
  ChangeManagement: 'bg-emerald-100 text-emerald-800',
  Technology: 'bg-violet-100 text-violet-800',
  Innovation: 'bg-amber-100 text-amber-800',
  Governance: 'bg-rose-100 text-rose-800',
}

interface Props {
  dashboard: DashType
  sessionId: string
  teamId: string
}

export default function Dashboard({ dashboard, sessionId, teamId }: Props) {
  const d = dashboard
  const historyQ = useQuery({
    queryKey: ['session-history', sessionId],
    queryFn: () => api.getSessionHistory(sessionId),
    refetchInterval: 15_000,
    enabled: !!sessionId,
  })
  const snapshots = historyQ.data?.teams[teamId]?.snapshots ?? []
  const totalQuarters = historyQ.data?.num_quarters ?? 20
  const series = {
    bvi: snapshots.map((s) => (s.stocks['Business Value Index'] ?? 0) * 19 / 1000),
    pipeline: snapshots.map((s) => s.stocks['Active Use Case Pipeline'] ?? 0),
    deployed: snapshots.map((s) => s.stocks['Deployed Use Cases'] ?? 0),
    budget: snapshots.map((s) => (s.stocks['Monthly Budget Stock'] ?? 0) * 3),
    confidence: snapshots.map((s) => s.stocks['Leadership Confidence'] ?? 0),
  }
  return (
    <div className="space-y-4">
      <div className="card">
        <h2 className="text-base font-semibold mb-3">This Quarter</h2>
        <div className="space-y-3">
          <Stat
            label="Business Value"
            value={`$${d.value_est_m.toFixed(1)}M`}
            series={series.bvi}
            totalQuarters={totalQuarters}
          />
          <AdoptionStat pct={d.workforce_adoption_pct} />
          <MaturityCompare
            you={d.ai_maturity_level}
            industry={d.industry_ai_maturity}
          />
        </div>
      </div>

      <div className="card">
        <h2 className="text-base font-semibold mb-3">Pipeline & Capacity</h2>
        <div className="space-y-3">
          <Stat
            label="Active Use Case Pipeline"
            value={d.pipeline.toFixed(1)}
            series={series.pipeline}
            totalQuarters={totalQuarters}
          />
          <Stat
            label="Deployed Use Cases"
            value={d.deployed_use_cases.toFixed(1)}
            series={series.deployed}
            totalQuarters={totalQuarters}
          />
          <Stat
            label="Quarterly Budget"
            value={`$${Math.round(d.monthly_budget_k * 3).toLocaleString('en-US')}K`}
            series={series.budget}
            totalQuarters={totalQuarters}
          />
        </div>
      </div>

      <div className="card">
        <h2 className="text-base font-semibold mb-3">Leadership & Risk</h2>
        <div className="space-y-3">
          <Stat
            label="Leadership Confidence"
            value={`${d.leadership_confidence.toFixed(0)} / 100`}
            tone={d.leadership_confidence < 30 ? 'danger' :
                  d.leadership_confidence < 50 ? 'warn' : 'ok'}
            series={series.confidence}
            totalQuarters={totalQuarters}
          />
          <RiskAlertStat level={d.risk_alert_level} />
        </div>
      </div>

      <div className="card">
        <h2 className="text-base font-semibold mb-3">
          Active Programs <span className="text-slate-500 font-normal">({d.active_programs.length})</span>
        </h2>
        {d.active_programs.length === 0 ? (
          <p className="text-sm text-slate-500">No programs running yet.</p>
        ) : (
          <div className="flex flex-wrap gap-1">
            {d.active_programs.map((id) => (
              <span key={id} className="text-xs font-mono px-2 py-0.5 bg-slate-200 rounded">
                {id}
              </span>
            ))}
          </div>
        )}
      </div>

      {d.pending_forced_activity && (
        <div className="card border-danger bg-red-50">
          <h2 className="text-sm font-semibold text-danger mb-1">
            Forced action this turn
          </h2>
          <p className="text-sm">
            An event requires you to take activity{' '}
            <span className="font-mono font-semibold">{d.pending_forced_activity}</span>{' '}
            this quarter.
          </p>
        </div>
      )}
    </div>
  )
}

function Stat({
  label, value, tone = 'neutral', series, totalQuarters,
}: {
  label: string
  value: string
  tone?: 'ok' | 'warn' | 'danger' | 'neutral'
  series?: number[]
  totalQuarters?: number
}) {
  const toneClass =
    tone === 'ok' ? 'text-ok' :
    tone === 'warn' ? 'text-warn' :
    tone === 'danger' ? 'text-danger' :
    'text-slate-900'
  const hasSparkline = series !== undefined && totalQuarters !== undefined
  return (
    <div>
      <div className="stat-label">{label}</div>
      <div className="flex items-end justify-between gap-3">
        <div
          className={`stat-value ${toneClass} ${
            hasSparkline ? 'w-28 shrink-0' : ''
          }`}
        >
          {value}
        </div>
        {hasSparkline && (
          <div className="flex-1 min-w-0 pb-1">
            <Sparkline values={series} totalQuarters={totalQuarters} />
          </div>
        )}
      </div>
    </div>
  )
}

function AdoptionStat({ pct }: { pct: number }) {
  const clamped = Math.max(0, Math.min(100, pct))
  return (
    <div>
      <div className="stat-label mb-1">Workforce Adoption</div>
      <div className="flex items-center gap-3">
        <span className="tabular-nums font-medium text-sm text-slate-900 w-28 shrink-0">
          {pct.toFixed(0)}%
        </span>
        <div className="flex-1 h-1.5 rounded-full bg-slate-200 overflow-hidden">
          <div className="h-full bg-brand-600" style={{ width: `${clamped}%` }} />
        </div>
      </div>
    </div>
  )
}

function RiskAlertStat({ level }: { level: number }) {
  const maxLevel = 3
  const toneText =
    level >= 2 ? 'text-danger' : level >= 1 ? 'text-warn' : 'text-ok'
  const fillBg =
    level >= 2 ? 'bg-danger' : level >= 1 ? 'bg-warn' : 'bg-slate-300'
  return (
    <div>
      <div className="stat-label">Risk Alert</div>
      <div className="flex items-center gap-3">
        <span className={`stat-value w-28 shrink-0 ${toneText}`}>
          Level {level}
        </span>
        <div className="flex-1 flex items-center gap-1">
          {Array.from({ length: maxLevel }, (_, i) => i + 1).map((i) => (
            <div
              key={i}
              className={`flex-1 h-1.5 rounded-full ${
                i <= level ? fillBg : 'bg-slate-200'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

function MaturityCompare({ you, industry }: { you: number; industry: number }) {
  const max = 4
  const pct = (v: number) => Math.max(0, Math.min(100, (v / max) * 100))
  return (
    <div>
      <div className="stat-label mb-2">AI Maturity</div>
      <div className="space-y-2">
        <MaturityRow label="You" value={you} pct={pct(you)} fill="bg-brand-600" />
        <MaturityRow
          label="Industry"
          value={industry}
          pct={pct(industry)}
          fill="bg-slate-400"
        />
      </div>
    </div>
  )
}

function MaturityRow({
  label, value, pct, fill,
}: {
  label: string
  value: number
  pct: number
  fill: string
}) {
  return (
    <div className="flex items-center gap-3 text-sm">
      <div className="w-28 shrink-0 flex items-baseline justify-between">
        <span className="text-slate-500">{label}</span>
        <span className="tabular-nums text-slate-900 font-medium">
          {value.toFixed(2)}
        </span>
      </div>
      <div className="flex-1 h-1.5 rounded-full bg-slate-200 overflow-hidden">
        <div className={`h-full ${fill}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  )
}
